import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  createElement,
  ReactNode,
} from 'react';
import { articles as articlesData } from '../mocks/articles';
import { resources as resourcesData } from '../mocks/resources';
import { useArticlesContext } from '../context/ArticlesContext';
import { supabase } from '../lib/supabase';
import { CMS_KEYS, fetchCmsKeys, upsertCmsKey, getAdminEmail } from '../lib/cmsStore';
import { DEFAULT_OG_IMAGE } from '../lib/headMeta';
import { isRetiredJob } from '../lib/retiredContent';

interface CMSContent {
  home: {
    heroMessages: Array<{
      headline: string;
      subheadline: string;
    }>;
    stats: {
      clientsServed: { value: string; label: string };
      satisfaction: { value: string; label: string };
      yearsExperience: { value: string; label: string };
      consultants: { value: string; label: string };
    };
  };
  about: {
    hero: {
      headline: string;
      subheadline: string;
      backgroundImage: string;
    };
    mission: {
      title: string;
      text: string;
    };
  };
  contact: {
    hero: {
      headline: string;
      subheadline: string;
      backgroundImage: string;
    };
    info: {
      address: string;
      phone: string;
      email: string;
      hours: string;
    };
    map: {
      embedUrl: string;
    };
  };
  services: Record<string, any>;
  industries: Record<string, any>;
}

interface CMSSettings {
  site: {
    name: string;
    tagline: string;
    logoUrl: string;
    faviconUrl: string;
    primaryColor: string;
  };
  navigation: {
    services: { items: any[] };
    industries: { items: any[] };
    insights: { items: any[] };
    company: { items: any[] };
    contact: { items: any[] };
  };
  footer: {
    copyright: string;
    social: {
      linkedin: string;
      twitter: string;
      facebook: string;
      instagram: string;
    };
  };
  seo: {
    titleTemplate: string;
    description: string;
    ogImage: string;
  };
}

interface HeroMessage {
  id: string;
  title: string;
  subtitle: string;
  category: 'innovation' | 'expertise' | 'results' | 'partnership';
  ctaText: string;
  ctaLink: string;
  active: boolean;
  // legacy compat
  text?: string;
}

interface Career {
  id: string;
  slug?: string;
  title: string;
  department: string;
  location: string;
  type: string;
  salaryRange?: string;
  description: string;
  requirements?: string[];
  responsibilities?: string[];
  benefits?: string[];
  active: boolean;
  postedDate?: string;
  closesAt?: string;
}

interface Candidate {
  id: string;
  careerId: string;
  careerTitle: string;
  name: string;
  email: string;
  phone?: string;
  linkedInUrl?: string;
  portfolioUrl?: string;
  coverLetter?: string;
  status: 'new' | 'reviewing' | 'phone_screen' | 'interview' | 'offer' | 'hired' | 'rejected' | 'archived';
  appliedDate: string;
  notes?: string;
  rating?: 1 | 2 | 3 | 4 | 5;
  source?: string;
  resumePath?: string;
  resumeUploadId?: string;
  uploadState?: string;
  isGeneralInterest?: boolean;
  chatConversationId?: string;
  reference?: string;
  submissionReference?: string;
  attachments?: Array<{ filename?: string; scan_state?: string; upload_id?: string; size_bytes?: number; source?: string }>;
  data?: Record<string, any>;
}

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  author: string;
  authorRole: string;
  authorAvatar: string;
  date: string;
  readTime: string;
  excerpt: string;
  image: string;
  tags: string[];
  body: string;
  featured: boolean;
  status: 'published' | 'draft' | 'archived';
  /** Set by the Blog Autopilot / AI create flow. */
  aiAssisted?: boolean;
  source?: 'ai' | 'manual';
  reviewStatus?: 'ready_for_review' | 'approved' | 'rejected' | null;
  scheduledPublishAt?: string | null;
  proposedPublishAt?: string | null;
  claimsCheck?: { passed: boolean; issues: string[] } | null;
  aiLog?: Array<Record<string, unknown>>;
  createdAt?: string;
  updatedAt?: string;
}

function readCache<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return fallback;
    }
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored) as T;
  } catch {
    // ignore malformed cache
  }
  return fallback;
}

function migrateHeroMessages(list: HeroMessage[]): HeroMessage[] {
  return (list || []).map((m) => ({
    ...m,
    title: m.title || m.text || '',
    subtitle: m.subtitle || '',
    ctaText: m.ctaText || 'Learn More',
    ctaLink: m.ctaLink || '/services',
    active: m.active !== undefined ? m.active : true,
  }));
}

function toCandidate(row: Record<string, any>): Candidate {
  const data = (row.data as Record<string, any>) || {};
  return {
    id: String(row.id),
    careerId: row.job_id || '',
    careerTitle: row.job_title || '',
    name: row.full_name || '',
    email: row.email || '',
    phone: row.phone || undefined,
    linkedInUrl: row.linkedin_url || undefined,
    portfolioUrl: data.portfolioUrl || undefined,
    coverLetter: row.cover_letter || undefined,
    status: (row.status || 'new') as Candidate['status'],
    appliedDate: row.created_at ? String(row.created_at).split('T')[0] : '',
    notes: row.notes || undefined,
    rating: (data.rating || undefined) as Candidate['rating'],
    source: data.source || row.source || undefined,
    resumePath: row.resume_path || undefined,
    resumeUploadId: row.resume_upload_id || undefined,
    isGeneralInterest: row.is_general_interest || false,
    chatConversationId: row.chat_conversation_id || undefined,
    reference: row.reference || undefined,
    submissionReference: data.submission_reference || undefined,
    attachments: Array.isArray(data.attachments) ? data.attachments : undefined,
    data,
  };
}

const defaultContent: CMSContent = {
  home: {
    heroMessages: [],
    stats: {
      clientsServed: { value: '500+', label: 'Clients Served' },
      satisfaction: { value: '98%', label: 'Client Satisfaction' },
      yearsExperience: { value: '27+', label: 'Years of Excellence' },
      consultants: { value: '1000+', label: 'Expert Consultants' }
    }
  },
  about: {
    hero: {
      headline: 'About Cendien',
      subheadline: 'Empowering businesses through innovative technology solutions and expert consulting services.',
      backgroundImage: ''
    },
    mission: {
      title: 'Our Mission',
      text: 'To deliver exceptional technology solutions that drive business transformation and success.'
    }
  },
  contact: {
    hero: {
      headline: 'Get in Touch',
      subheadline: 'Let\'s discuss how we can help transform your business with our expert solutions.',
      backgroundImage: ''
    },
    info: {
      address: '123 Business Ave, Suite 100, City, ST 12345',
      phone: '(555) 123-4567',
      email: 'contact@cendien.com',
      hours: 'Monday - Friday: 9:00 AM - 6:00 PM EST'
    },
    map: {
      embedUrl: ''
    }
  },
  services: {},
  industries: {}
};

// Page content stored separately keyed by pageId
const defaultPageContent: Record<string, any> = {
  home: {
    hero: { messages: [], headline: '', subheadline: '' },
    stats: [
      { value: '500+', label: 'Clients Served' },
      { value: '98%', label: 'Client Satisfaction' },
      { value: '27+', label: 'Years of Excellence' },
      { value: '1000+', label: 'Expert Consultants' },
    ],
  },
  about: {
    hero: { headline: 'About Cendien', subheadline: 'Empowering businesses through innovative technology solutions.', backgroundImage: '' },
    mission: { title: 'Our Mission', text: 'To deliver exceptional technology solutions that drive business transformation.' },
  },
  contact: {
    hero: { headline: 'Get in Touch', subheadline: 'Let\'s discuss how we can help transform your business.', backgroundImage: '' },
    info: { address: '123 Business Ave, Suite 100, City, ST 12345', phone: '(555) 123-4567', email: 'contact@cendien.com', hours: 'Monday - Friday: 9:00 AM - 6:00 PM EST' },
    map: { embedUrl: '' },
  },
  services: { hero: { headline: 'Our Services', subheadline: 'Comprehensive IT solutions tailored to your needs.', backgroundImage: '' } },
  industries: { hero: { headline: 'Industries We Serve', subheadline: 'Deep expertise across key verticals.', backgroundImage: '' } },
  careers: { hero: { headline: 'Join Our Team', subheadline: 'Build your career with Cendien.', backgroundImage: '' } },
};

const defaultSettings: CMSSettings = {
  site: {
    name: 'Cendien',
    tagline: 'Enterprise Solutions & IT Staffing',
    logoUrl: 'https://static.readdy.ai/image/8e0505aa0635a0a18473d6b2a0ce03ab/0bf003290bf18bcaec5a87fd6a2734a3.png',
    faviconUrl: 'https://static.readdy.ai/image/8e0505aa0635a0a18473d6b2a0ce03ab/0bf003290bf18bcaec5a87fd6a2734a3.png',
    primaryColor: '#14B8A6'
  },
  navigation: {
    services: { items: [] },
    industries: { items: [] },
    insights: { items: [] },
    company: { items: [] },
    contact: { items: [] }
  },
  footer: {
    copyright: '© 2024 Cendien. All rights reserved.',
    social: {
      linkedin: 'https://linkedin.com/company/cendien',
      twitter: 'https://twitter.com/cendien',
      facebook: 'https://facebook.com/cendien',
      instagram: 'https://instagram.com/cendien'
    }
  },
  seo: {
    titleTemplate: '%s | Cendien',
    description: 'Enterprise IT solutions, ERP consulting, and staffing services that drive business transformation.',
    ogImage: DEFAULT_OG_IMAGE
  }
};

export const defaultCareers: Career[] = [
  {
    id: 'career-it-consultant-2025',
    title: 'IT Consultant — Computer/Network Support',
    department: 'Cloud & Infrastructure',
    location: 'Carrollton, Texas',
    type: 'Full-time',
    description: "Join Cendien's Cloud & Infrastructure practice as an IT Consultant specializing in computer and network support. You will troubleshoot cross-cloud and hybrid environments, maintain network hardware and cloud counterparts, and implement security controls across enterprise platforms. This role combines hands-on technical work with strategic architecture input for Fortune 500 and public-sector clients.",
    requirements: [
      "Bachelor's or higher in Computer Science, Computer Engineering, Computer/Information Systems, Management Information Systems, or a closely related computing/information-systems discipline",
      'Two or more years of professional experience in cloud, network or infrastructure engineering',
      'Working knowledge of computer networking and protocols, operating systems, cloud architecture across multiple providers, identity & access management, and information security',
      'Demonstrated programming ability in Python and at least one shell or scripting language, plus practical experience with CI/CD pipelines',
      'Preferred: concurrent experience across Azure, AWS and Google Cloud; experience integrating SharePoint, workflow platforms, search services or vendor AI endpoints; relevant vendor certifications',
    ],
    responsibilities: [
      'Troubleshoot cross-cloud and hybrid network connectivity across Microsoft Azure, Amazon Web Services Cloud and enterprise platforms integrated with them',
      'Write and maintain deployment scripts and infrastructure-as-code definitions, including secret injection, configuration, health gates and rollback',
      'Design and program Python-based cloud integration and integration services including data ingestion, optical recognition, service orchestration',
      'Provide tier-2 and tier-3 support across cloud, identity and integration layers',
      'Install, configure and maintain network hardware and its cloud counterparts including managed firewalls, VPN, wireless services and virtual networks',
      'Monitor network and service performance and security, design dashboards, log queries and alert thresholds',
      'Monitor scheduled maintenance and dependency operating-system updates',
      'Produce and maintain group, service account, operations, and configuration rotation, invocations and runbooks',
      'Implement network, transport, identity and secrets controls, including firewall security-group rules, TLS and certificate authentication and encryption',
      'Collaborate with the internal team, cloud and software vendors, and client vendors on architecture, integration and escalations',
      'Research, test and evaluate network, cloud and integration technologies; build proof of concept and benchmark them before adoption',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Remote flexibility', 'Certification reimbursement'],
    active: true,
    closesAt: '2027-12-31',
  },
  {
    id: 'career-workday-technical',
    title: 'Workday Technical Consultant',
    department: 'Workday Practice',
    location: 'Dallas, Texas',
    type: 'Full-time',
    description: "Join Cendien's Workday practice as a Technical Consultant focused on Workday's integration, reporting and configuration toolkit. You will design and build Workday integrations using Workday Studio, EIBs, Core Connectors, Cloud Connect and RaaS, while also extending the platform with calculated fields, custom reports and security configuration for enterprise HCM and Financials environments.",
    requirements: [
      'Workday certification in Integrations, Studio, Reporting or equivalent hands-on experience',
      '3+ years configuring Workday HCM, Financials or Payroll modules',
      'Strong command of Workday Studio, Enterprise Interface Builder (EIB), Core Connectors and Report-as-a-Service (RaaS)',
      'Experience with XSLT, XML, Web Services (SOAP/REST) and middleware platforms',
      'Working knowledge of calculated fields, business processes, security groups and domain security policies',
      'Excellent analytical and troubleshooting skills with a client-first mindset',
    ],
    responsibilities: [
      'Design, build and test Workday integrations including inbound/outbound EIBs, Studio integrations and Cloud Connect connectors',
      'Develop custom reports, dashboards and calculated fields to support business intelligence requirements',
      'Configure and troubleshoot business processes, security policies and notifications',
      'Collaborate with functional consultants to translate business requirements into technical specifications',
      'Perform unit, integration and regression testing during release cycles and Workday feature updates',
      'Document integration architecture, mappings and operational runbooks for clients',
      'Support go-live cutover, hypercare and post-production stabilization',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Workday certification reimbursement', 'Remote flexibility', 'Professional development'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-workday-functional',
    title: 'Workday Functional Consultant',
    department: 'Workday Practice',
    location: 'Remote / Dallas, Texas',
    type: 'Full-time',
    description: "Cendien is seeking a Workday Functional Consultant to lead configuration and deployment of Workday HCM, Compensation, Talent and Absence modules. You will partner with clients to gather requirements, configure business processes, and guide testing and change management across deployment and post-go-live support.",
    requirements: [
      'Workday HCM certification (or equivalent hands-on configuration experience)',
      '3+ years configuring Workday HCM, Compensation, Talent, Time Tracking or Absence modules',
      'Strong understanding of HR processes and best practices across hire-to-retire',
      'Experience leading requirements workshops and design sessions with stakeholders',
      'Ability to translate business needs into functional designs and configuration',
      'Excellent communication, presentation and documentation skills',
    ],
    responsibilities: [
      'Lead requirements gathering and fit/gap analysis workshops with client stakeholders',
      'Configure Workday HCM, business processes, condition rules and security in alignment with design decisions',
      'Build and maintain functional specifications, configuration workbooks and test scenarios',
      'Guide clients through unit testing, user acceptance testing and change management',
      'Support data conversion, validation and tenant management activities',
      'Provide post-go-live hypercare support and iterative optimization recommendations',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Workday certification reimbursement', 'Remote flexibility', 'Professional development'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-workday-developer',
    title: 'Workday Integration Developer',
    department: 'Workday Practice',
    location: 'Dallas, Texas',
    type: 'Full-time',
    description: "Cendien is hiring a Workday Integration Developer to design, develop and support complex integrations across the Workday ecosystem. This role is deeply hands-on, building and maintaining Studio integrations, XSLT transforms, RaaS reports and orchestration workflows that connect Workday with payroll, benefits and downstream enterprise systems.",
    requirements: [
      '3+ years developing Workday integrations (Studio, EIB, Core Connectors, RaaS, PECI/PICOF)',
      'Expertise in XSLT, XML, JSON, SOAP and REST web services',
      'Experience with middleware and iPaaS platforms (Workday Extend, Dell Boomi, MuleSoft, etc.)',
      'Working knowledge of Workday HCM and Payroll data models',
      'Strong debugging, performance tuning and monitoring skills',
      'Familiarity with version control and CI/CD practices is a plus',
    ],
    responsibilities: [
      'Develop, test and deploy Workday Studio integrations and orchestration workflows',
      'Write and maintain XSLT transforms for complex data mapping between Workday and external systems',
      'Build RaaS reports and REST/SOAP integrations for real-time and scheduled data exchange',
      'Troubleshoot integration failures, monitor error queues and implement corrective actions',
      'Optimize integration performance and ensure data quality and security compliance',
      'Collaborate with technical and functional teams to align on integration architecture',
      'Maintain detailed technical documentation and operational runbooks',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Workday certification reimbursement', 'Remote flexibility', 'Professional development'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-infor-consultant',
    title: 'Infor Consultant',
    department: 'ERP Solutions',
    location: 'Remote',
    type: 'Full-time',
    description: "Cendien is looking for an Infor Consultant to support enterprise Infor CloudSuite implementations and optimizations across manufacturing, healthcare and public-sector clients. You will configure Infor modules, lead client workshops, and ensure successful go-lives by bridging business process and system configuration.",
    requirements: [
      '3+ years implementing or supporting Infor CloudSuite (LN, M3, HCM, FSM, or related)',
      'Strong understanding of enterprise ERP processes (finance, supply chain, HR or field service)',
      'Experience with Infor OS, ION, or related integration technologies is a plus',
      'Proven ability to lead requirements and design sessions with client teams',
      'Infor certifications strongly preferred',
      'Excellent communication and documentation skills',
    ],
    responsibilities: [
      'Configure and deploy Infor CloudSuite modules to meet client requirements',
      'Lead fit/gap analysis, process design and configuration workshops',
      'Support data migration, testing and user training activities',
      'Troubleshoot functional issues and recommend process or configuration changes',
      'Collaborate with technical teams on integrations and customizations',
      'Provide go-live and hypercare support to ensure smooth transitions',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Infor certification reimbursement', 'Remote work', 'Professional development'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-infor-developer',
    title: 'Infor Developer',
    department: 'ERP Solutions',
    location: 'Remote',
    type: 'Full-time',
    description: "Cendien is hiring an Infor Developer to build customizations, reports and integrations within the Infor ecosystem. You will extend Infor CloudSuite applications using Infor OS, Mongoose, ION workflows, and custom development to meet enterprise client needs.",
    requirements: [
      '3+ years developing on Infor platforms (Infor OS, Mongoose, ION, M3/LN customization)',
      'Proficiency in SQL and experience with Infor data models',
      'Experience building ION workflows, APIs and integrations',
      'Familiarity with Infor reporting and business intelligence tools',
      'Strong problem-solving and debugging skills',
      'Experience with version control and agile delivery is a plus',
    ],
    responsibilities: [
      'Develop customizations and extensions using Infor OS and Mongoose',
      'Build and maintain ION workflows and integrations between Infor and third-party systems',
      'Create custom reports, forms and data extracts to support business needs',
      'Troubleshoot and resolve technical issues across the Infor stack',
      'Optimize performance of customizations and integrations',
      'Document technical designs and maintain development standards',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Infor certification reimbursement', 'Remote work', 'Professional development'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-ai-consultant-dallas',
    title: 'AI Consultant',
    department: 'AI & Innovation',
    location: 'Dallas, Texas',
    type: 'Full-time',
    description: "Cendien is seeking an AI Consultant based in Dallas to help enterprise clients design, build and operationalize AI and machine learning solutions. You will work across generative AI, predictive analytics, and intelligent automation, guiding clients from strategy through production deployment.",
    requirements: [
      '4+ years experience in AI/ML, data science or applied analytics',
      'Proficiency in Python and modern ML frameworks (PyTorch, TensorFlow, or scikit-learn)',
      'Hands-on experience with LLMs, RAG pipelines, or generative AI services',
      'Experience with cloud AI platforms (AWS Bedrock/SageMaker, Azure OpenAI, or GCP Vertex AI)',
      'Strong client-facing and consulting skills with the ability to translate business needs into technical solutions',
      'Located in or willing to work on-site in the Dallas-Fort Worth metroplex',
    ],
    responsibilities: [
      'Lead AI discovery workshops and help clients identify high-impact use cases',
      'Design and build AI/ML solutions including LLM applications, predictive models and automation',
      'Develop proof-of-concepts and pilots to validate business value',
      'Advise clients on AI governance, responsible AI practices and MLOps',
      'Collaborate with data engineering and cloud teams to operationalize models',
      'Communicate technical concepts and outcomes to executive stakeholders',
    ],
    benefits: ['Competitive salary', 'Stock options', 'Health benefits', 'Learning budget', 'Hybrid work'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-sysadmin-austin',
    title: 'Systems Administrator',
    department: 'Cloud & Infrastructure',
    location: 'Austin, Texas',
    type: 'Full-time',
    description: "Cendien is hiring a Systems Administrator for the Austin area to manage, monitor and maintain enterprise IT infrastructure for our clients. You will oversee servers, cloud environments, identity and access management, and provide tier-2/3 support across hybrid environments.",
    requirements: [
      '3+ years as a Systems Administrator or similar infrastructure role',
      'Strong knowledge of Windows Server and/or Linux administration',
      'Experience with Microsoft 365, Active Directory, Entra ID and Exchange',
      'Working knowledge of cloud platforms (Azure, AWS or GCP) and virtualization (VMware/Hyper-V)',
      'Familiarity with networking fundamentals, firewalls and VPN',
      'Excellent troubleshooting and customer-service skills',
      'Located in the Austin metro area with ability to travel to client sites as needed',
    ],
    responsibilities: [
      'Install, configure and maintain servers, storage and cloud resources',
      'Administer Active Directory, Entra ID, and user/group access management',
      'Monitor system performance, security and availability; respond to alerts',
      'Manage backups, patching, and OS/application updates',
      'Provide tier-2/3 support and resolve escalated infrastructure issues',
      'Document configurations, procedures and troubleshooting runbooks',
      'Support client sites across the greater Austin area',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Hybrid work', 'Certification reimbursement'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-app-dev-oklahoma',
    title: 'Application Developer',
    department: 'Application Development',
    location: 'Oklahoma City, Oklahoma',
    type: 'Full-time',
    description: "Cendien is seeking an Application Developer to support the State of Oklahoma's enterprise application portfolio. You will design, develop and maintain custom applications and integrations that support state agencies, working within an agile team to modernize legacy systems and deliver secure, citizen-facing digital services.",
    requirements: [
      "Bachelor's degree in Computer Science, Information Systems or a related field",
      '3+ years of full-stack application development experience',
      'Proficiency in .NET, C#, Java or a similar enterprise language',
      'Experience with SQL databases (SQL Server, Oracle or PostgreSQL)',
      'Working knowledge of REST APIs, web services and integration patterns',
      'Familiarity with cloud platforms (Azure, AWS) and CI/CD tooling',
      'State or local government experience is a plus',
    ],
    responsibilities: [
      'Design, develop and maintain web and enterprise applications for state agencies',
      'Write clean, secure and maintainable code following established standards',
      'Build and consume REST APIs and integrate with legacy and third-party systems',
      'Collaborate with business analysts and stakeholders to refine requirements',
      'Perform unit testing, code reviews and support quality assurance',
      'Troubleshoot and resolve production issues in a timely manner',
      'Document technical designs and maintain development standards',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Remote flexibility', 'Certification reimbursement'],
    active: true,
    postedDate: '2026-07-28',
    closesAt: '2026-12-31',
  },
  {
    id: 'career-ba-oklahoma',
    title: 'Business Analyst',
    department: 'Business Analysis',
    location: 'Oklahoma City, Oklahoma',
    type: 'Full-time',
    description: "Cendien is seeking a Business Analyst to support the State of Oklahoma's IT modernization initiatives. You will bridge the gap between agency stakeholders and technical teams, eliciting requirements, documenting business processes, and ensuring solutions deliver measurable value to state agencies and citizens.",
    requirements: [
      "Bachelor's degree in Business, Information Systems or a related field",
      '3+ years of business analysis experience in enterprise or government environments',
      'Strong requirements elicitation, process modeling and documentation skills',
      'Experience with Agile/Scrum and SDLC methodologies',
      'Proficiency with Jira, Visio and Microsoft Office',
      'Excellent communication, facilitation and stakeholder management skills',
      'Public sector or state government experience is a plus',
    ],
    responsibilities: [
      'Elicit, analyze and document business and functional requirements',
      'Facilitate workshops and interviews with agency stakeholders',
      'Model current and future state business processes',
      'Create user stories, acceptance criteria and functional specifications',
      'Support user acceptance testing and change management activities',
      'Track and communicate project status, risks and dependencies',
      'Ensure delivered solutions align with business objectives',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Remote flexibility', 'Certification reimbursement'],
    active: true,
    postedDate: '2026-06-19',
    closesAt: '2026-12-31',
  },
  {
    id: 'career-ai-specialist-oakland',
    title: 'AI Specialist',
    department: 'AI & Innovation',
    location: 'Pontiac, Michigan',
    type: 'Full-time',
    description: "Cendien is seeking an AI Specialist to support Oakland County's digital transformation with artificial intelligence and machine learning solutions. You will identify high-impact use cases, build AI prototypes and production solutions, and help the county harness AI to improve service delivery and operational efficiency.",
    requirements: [
      "Bachelor's degree in Computer Science, Data Science or a related field (Master's preferred)",
      '3+ years of experience in AI/ML, data science or applied analytics',
      'Proficiency in Python and ML frameworks (scikit-learn, PyTorch, TensorFlow)',
      'Experience with LLMs, natural language processing and/or computer vision',
      'Working knowledge of cloud AI services (Azure AI, AWS or GCP)',
      'Strong analytical, problem-solving and communication skills',
      'Government or public sector experience is a plus',
    ],
    responsibilities: [
      'Identify and prioritize AI use cases across county departments',
      'Design, build and deploy AI/ML models and intelligent automation',
      'Develop proof-of-concepts to validate business value and feasibility',
      'Advise on AI governance, data privacy and responsible AI practices',
      'Collaborate with data engineering and IT teams to operationalize solutions',
      'Present findings and recommendations to county leadership',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Hybrid work', 'Certification reimbursement'],
    active: true,
    postedDate: '2026-08-05',
    closesAt: '2026-12-31',
  },
  {
    id: 'career-ai-specialist-oakland-hybrid',
    title: 'AI Specialist',
    department: 'AI & Innovation',
    location: 'Oakland, Michigan (Hybrid)',
    type: 'Full-time',
    description: "Cendien is seeking a full-time, hybrid AI Specialist to support Oakland County, Michigan's technology and digital transformation initiatives. You will bridge the gap between county stakeholders and technical teams by eliciting and validating business needs, analyzing current processes and systems, and translating requirements into actionable technical solutions. Position Type: Hybrid — minimum 2 days per week onsite; remote days require an adequate home office following standard County work hours.",
    requirements: [
      '5+ years of experience as a Business Analyst / Business Systems Analyst',
      'Strong experience using the M365 Productivity Suite',
      'Proven experience working on IT Infrastructure projects',
      'Demonstrated problem-solving skills, including issue identification, obstacle resolution, and solution implementation',
      'Excellent written and verbal communication skills',
      'Ability to successfully multi-task and prioritize work across active projects',
      'Passionate about delivering the ultimate customer experience',
    ],
    responsibilities: [
      'Elicit, analyze, specify, and validate the business needs of project stakeholders through interviewing stakeholders and analyzing current business processes and systems',
      'Apply proven communication, analytical, and problem-solving skills to support projects and ensure deliverables are met according to specifications',
      'Perform data analysis, design solutions, and write technical requirements',
      'Test and implement technology solutions',
      'Conduct research and create procedures to improve existing systems',
      'Provide training and support to users',
      'Write Requests for Proposals (RFPs) and Contract Scope of Deliverables',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Hybrid work', 'Certification reimbursement'],
    active: true,
    postedDate: '2026-08-18',
    closesAt: '2026-12-31',
  },
  {
    id: 'career-ba-oakland-mi',
    title: 'Business Analyst',
    department: 'Business Analysis',
    location: 'Pontiac, Michigan',
    type: 'Full-time',
    description: "Cendien is seeking a Business Analyst to support Oakland County, Michigan's IT and digital services initiatives. You will partner with county departments to gather requirements, document processes, and translate business needs into actionable technical solutions that improve services for residents.",
    requirements: [
      "Bachelor's degree in Business, Information Systems or a related field",
      '3+ years of business analysis experience in enterprise or government settings',
      'Strong skills in requirements elicitation, process mapping and documentation',
      'Experience with Agile/Scrum and SDLC methodologies',
      'Proficiency with Jira, Visio, SharePoint and Microsoft Office',
      'Excellent facilitation and stakeholder communication skills',
      'Local government or public sector experience is a plus',
    ],
    responsibilities: [
      'Elicit, analyze and document business requirements from county stakeholders',
      'Facilitate discovery workshops and process mapping sessions',
      'Create functional specifications, user stories and acceptance criteria',
      'Support testing, training and change management activities',
      'Identify process improvement and automation opportunities',
      'Communicate project progress, risks and recommendations to leadership',
    ],
    benefits: ['Health insurance', '401(k) matching', 'Professional development', 'Hybrid work', 'Certification reimbursement'],
    active: true,
    postedDate: '2026-07-11',
    closesAt: '2026-12-31',
  },
  {
    id: 'career-recruiter-dallas',
    title: 'Recruiter',
    department: 'Talent Acquisition',
    location: 'Dallas, Texas',
    type: 'Contract',
    description: "Cendien is seeking a driven Recruiter to join our Talent Acquisition team on a contract basis. You will own the full-cycle recruitment process for IT, ERP, and AI consulting roles, partnering with hiring managers to source, screen, and place top-tier talent across our client engagements.",
    requirements: [
      '2+ years of full-cycle recruiting experience, preferably in IT or professional services',
      'Proficiency with applicant tracking systems (ATS) and sourcing tools (LinkedIn Recruiter, Boolean search)',
      'Strong sourcing and candidate pipeline development skills',
      'Excellent communication, negotiation and relationship-building skills',
      'Ability to manage multiple requisitions and priorities in a fast-paced environment',
    ],
    responsibilities: [
      'Manage the end-to-end recruitment lifecycle from intake to offer acceptance',
      'Source active and passive candidates through job boards, social media and referrals',
      'Screen resumes, conduct phone interviews and coordinate the interview process',
      'Partner with hiring managers to define role requirements and candidate profiles',
      'Maintain accurate candidate records and pipeline data in the ATS',
      'Provide a positive candidate experience through timely communication and feedback',
    ],
    benefits: ['Competitive contract rate', 'Flexible schedule', 'Remote-friendly', 'Performance incentives'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-technical-recruiter',
    title: 'Technical Recruiter',
    department: 'Talent Acquisition',
    location: 'Remote',
    type: 'Contract',
    description: "Cendien is hiring a Technical Recruiter to source and place specialized IT, ERP and AI consultants. In this contract role you will focus on hard-to-fill technical positions, leveraging your understanding of enterprise technology stacks to identify and engage qualified candidates across the country.",
    requirements: [
      '3+ years of technical recruiting experience, ideally in IT staffing or consulting',
      'Working knowledge of enterprise technology roles (ERP, cloud, AI/ML, infrastructure)',
      'Expertise in advanced sourcing techniques and passive candidate engagement',
      'Experience with Workday, Infor, and enterprise software recruiting is a strong plus',
      'Strong organizational skills and attention to detail',
    ],
    responsibilities: [
      'Source and engage top technical talent for contract and full-time placements',
      'Build and maintain a robust pipeline of qualified candidates',
      'Conduct technical screens and assess candidate skill alignment',
      'Collaborate with account managers to understand client technical requirements',
      'Negotiate offers and manage candidate expectations through placement',
      'Track recruiting metrics and continuously improve sourcing strategies',
    ],
    benefits: ['Competitive contract rate', 'Remote work', 'Flexible hours', 'Performance-based incentives'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-sales-support-specialist',
    title: 'Sales Support Specialist',
    department: 'Sales & Business Development',
    location: 'Dallas, Texas',
    type: 'Part-time',
    description: "Cendien is looking for a Sales Support Specialist to join our business development team on a part-time basis. You will provide critical administrative and operational support to the sales team, helping to qualify leads, prepare proposals, and keep the sales pipeline running smoothly.",
    requirements: [
      '1+ years of experience in sales support, sales operations or administrative roles',
      'Proficiency with CRM tools (Salesforce or similar) and Microsoft Office',
      'Strong written and verbal communication skills',
      'Excellent attention to detail and organizational abilities',
      'Ability to work independently and manage time effectively',
    ],
    responsibilities: [
      'Support the sales team with lead qualification and data entry in the CRM',
      'Assist in preparing proposals, presentations and sales collateral',
      'Coordinate scheduling and follow-up for sales meetings and client calls',
      'Maintain accurate records of client interactions and sales activities',
      'Generate reports and track key sales metrics for leadership',
      'Provide administrative support across the business development lifecycle',
    ],
    benefits: ['Flexible part-time schedule', 'Competitive hourly pay', 'Professional development', 'Potential for full-time conversion'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-finance-intern',
    title: 'Finance Intern',
    department: 'Finance',
    location: 'Dallas, Texas',
    type: 'Internship',
    description: "Cendien is offering a Finance Internship for students and recent graduates looking to gain hands-on experience in corporate finance and accounting. You will support the finance team with financial analysis, reporting, budgeting and day-to-day accounting operations in a dynamic consulting environment.",
    requirements: [
      'Currently pursuing or recently completed a degree in Finance, Accounting, Economics or a related field',
      'Strong analytical skills and proficiency in Microsoft Excel',
      'Familiarity with financial statements and basic accounting principles',
      'Excellent attention to detail and organizational skills',
      'Strong written and verbal communication abilities',
    ],
    responsibilities: [
      'Assist with financial reporting, budgeting and variance analysis',
      'Support month-end close and reconciliation activities',
      'Help prepare financial models and ad-hoc analysis for leadership',
      'Maintain financial records and assist with data entry',
      'Participate in cross-functional projects and process improvement initiatives',
    ],
    benefits: ['Paid internship', 'Hands-on mentorship', 'Real-world project exposure', 'Potential for full-time conversion'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-sales-intern',
    title: 'Sales Intern',
    department: 'Sales & Business Development',
    location: 'Dallas, Texas',
    type: 'Internship',
    description: "Cendien is seeking a Sales Intern to support our business development team and gain real-world experience in B2B technology sales. You will assist with lead generation, prospecting, CRM management and client outreach, learning the full sales cycle in enterprise IT and consulting.",
    requirements: [
      'Currently pursuing or recently completed a degree in Business, Marketing, Communications or a related field',
      'Strong interpersonal and communication skills',
      'Interest in technology, consulting and B2B sales',
      'Proficiency with Microsoft Office and a willingness to learn CRM tools',
      'Self-motivated with a positive, team-oriented attitude',
    ],
    responsibilities: [
      'Assist with lead research, prospecting and list building',
      'Support the sales team with outreach and follow-up activities',
      'Maintain and update prospect and client data in the CRM',
      'Help prepare sales presentations and supporting materials',
      'Participate in sales meetings and learn the end-to-end sales process',
    ],
    benefits: ['Paid internship', 'Sales mentorship', 'Networking opportunities', 'Potential for full-time conversion'],
    active: true,
    closesAt: '2026-12-31',
  },
  {
    id: 'career-marketing-intern',
    title: 'Marketing Intern',
    department: 'Marketing',
    location: 'Dallas, Texas',
    type: 'Internship',
    description: "Cendien is looking for a creative Marketing Intern to support our marketing team across content creation, social media, and digital campaigns. You will help build the Cendien brand and gain hands-on experience across multiple marketing disciplines in the technology and consulting space.",
    requirements: [
      'Currently pursuing or recently completed a degree in Marketing, Communications, Business or a related field',
      'Strong writing and content creation skills',
      'Familiarity with social media platforms and digital marketing tools',
      'Basic understanding of SEO, email marketing or analytics is a plus',
      'Creative mindset with strong attention to detail',
    ],
    responsibilities: [
      'Assist in creating content for the blog, social media and marketing collateral',
      'Support digital marketing campaigns and social media scheduling',
      'Help with market research and competitive analysis',
      'Assist in tracking and reporting marketing metrics',
      'Collaborate with the team on brand and campaign initiatives',
    ],
    benefits: ['Paid internship', 'Creative mentorship', 'Portfolio-building projects', 'Potential for full-time conversion'],
    active: true,
    closesAt: '2026-12-31',
  },
];

const defaultHeroMessages: HeroMessage[] = [
  {
    id: 'hero-slide-1',
    title: 'Transform Your Business with AI & Cloud Innovation',
    subtitle: 'Enterprise-grade solutions powered by cutting-edge technology',
    category: 'innovation',
    ctaText: 'Explore AI Solutions',
    ctaLink: '/services/ai',
    active: true,
  },
  {
    id: 'hero-slide-2',
    title: 'Expert IT Talent & Managed Services',
    subtitle: 'Scale your team with top-tier consultants and dedicated support',
    category: 'expertise',
    ctaText: 'Find Talent',
    ctaLink: '/services/staffing',
    active: true,
  },
  {
    id: 'hero-slide-3',
    title: 'Enterprise ERP & Digital Transformation',
    subtitle: 'Modernize operations with Infor, Microsoft & AWS partnerships',
    category: 'results',
    ctaText: 'View ERP Services',
    ctaLink: '/services/erp',
    active: true,
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CMSContentContext = createContext<any>(null);

export function CMSContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<CMSContent>(() => readCache('cms_content', defaultContent));

  // Page content state
  const [pageContent, setPageContent] = useState<Record<string, any>>(() =>
    readCache('cms_page_content', defaultPageContent)
  );

  // Articles from Supabase context + mock articles merged
  const {
    articles: supabaseArticles,
    addArticle: addSupabaseArticle,
    updateArticle: updateSupabaseArticle,
    archiveArticle: archiveSupabaseArticle,
    loading: articlesLoading,
  } = useArticlesContext();

  // Merge: Supabase CMS articles + mock articles (mocks shown for public site, CMS articles for admin)
  // Admin and public pages use articles — supabase ones take priority by ID
  const supabaseIds = new Set(supabaseArticles.map((a) => a.id));
  // Also filter by slug — when a built-in article is edited and promoted to CMS,
  // the CMS version (same slug) shadows the mock so it doesn't appear twice.
  const supabaseSlugs = new Set(supabaseArticles.map((a) => a.slug));

  // Normalize mock articles to CMSArticle shape so they display correctly everywhere
  const normalizedMockArticles = articlesData
    .filter((a) => !supabaseIds.has(String(a.id)) && !supabaseSlugs.has(a.slug))
    .map((a) => ({
      id: String(a.id),
      title: a.title,
      slug: a.slug,
      category: a.category,
      // Uniform house byline — no fictional authors or photos.
      author: {
        name: 'Cendien Team',
        role: 'Cendien Team',
        avatar: '',
      },
      date: a.date,
      readTime: parseInt(String(a.readTime), 10) || 5,
      excerpt: a.excerpt,
      image: a.image,
      body: [
        a.content.intro,
        ...a.content.sections.map((s) =>
          [
            `## ${s.heading}`,
            s.body,
            s.bullets ? s.bullets.map((b) => `- ${b}`).join('\n') : '',
          ]
            .filter(Boolean)
            .join('\n\n')
        ),
        a.content.conclusion,
      ].join('\n\n'),
      tags: a.tags,
      status: 'published' as const,
      seo: {
        metaTitle: a.title,
        metaDescription: a.excerpt,
        focusKeyword: a.tags[0] || '',
        ogImage: a.image,
        canonical: '',
        noIndex: false,
      },
      scheduledPublishAt: null,
      viewCount: 0,
      engagementScore: 0,
    }));

  const articles = [...supabaseArticles, ...normalizedMockArticles];

  // Resources state
  const [resources, setResources] = useState(() => readCache('cms_resources', resourcesData));

  // Blog posts state (published + drafts; drafts are stored under a separate key)
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(() => readCache<BlogPost[]>('cms_blog_posts', []));

  // Settings state
  const [settings, setSettings] = useState<CMSSettings>(() => readCache('cms_settings', defaultSettings));

  // Hero messages state
  const [heroMessages, setHeroMessages] = useState<HeroMessage[]>(() =>
    migrateHeroMessages(readCache('cms_hero_messages', defaultHeroMessages))
  );

  // Careers state
  const [careers, setCareers] = useState<Career[]>(() =>
    (readCache('cms_careers', defaultCareers) as Career[]).filter((c) => !isRetiredJob(c.id)),
  );

  // Candidates come from public.job_applications (anonymous insert; admins read/update)
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  // Admin identity + persistence plumbing
  const adminEmailRef = useRef<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const lastSaved = useRef<Record<string, string>>({});

  const persist = useCallback(
    (key: string, value: unknown) => {
      if (!isAdmin) return;
      let serialized = '';
      try {
        serialized = JSON.stringify(value);
      } catch {
        return;
      }
      if (lastSaved.current[key] === serialized) return;
      lastSaved.current[key] = serialized;
      if (timers.current[key]) clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => {
        upsertCmsKey(key, value, adminEmailRef.current).catch(() => {
          // persistence failure is non-fatal — localStorage cache is the fallback
        });
      }, 600);
    },
    [isAdmin],
  );

  const loadCandidates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data || []) as Record<string, any>[];
      // Attach the live resume-upload state so chat resumes show "Pending review"
      // (scan_unavailable) at a glance in the Candidates list.
      const uploadIds = Array.from(
        new Set(rows.map((r) => r.resume_upload_id).filter(Boolean).map((id) => String(id)))
      );
      const stateMap: Record<string, string> = {};
      if (uploadIds.length) {
        try {
          const { data: ups } = await supabase.from('resume_uploads').select('id, state').in('id', uploadIds);
          (ups || []).forEach((u: Record<string, any>) => { stateMap[String(u.id)] = String(u.state); });
        } catch {
          // recruiter RLS may hide resume_uploads — the badge simply won't show
        }
      }
      setCandidates(rows.map((r) => {
        const candidate = toCandidate(r);
        if (r.resume_upload_id) candidate.uploadState = stateMap[String(r.resume_upload_id)];
        return candidate;
      }));
    } catch {
      // anonymous visitors cannot read applications — the list simply stays empty
    }
  }, []);

  // Snapshot the first-render values so the load pass never overwrites the DB
  useEffect(() => {
    const initialResources = (resources || []) as any[];
    const initialBlogPosts = (blogPosts || []) as BlogPost[];
    lastSaved.current = {
      content: JSON.stringify(content),
      page_content: JSON.stringify(pageContent),
      resources: JSON.stringify(initialResources.filter((r) => (r.status || 'published') !== 'draft' && (r.status || 'published') !== 'archived')),
      resources_drafts: JSON.stringify(initialResources.filter((r) => (r.status === 'draft' || r.status === 'archived'))),
      blog_posts: JSON.stringify(initialBlogPosts.filter((b) => (b.status || 'published') !== 'draft' && (b.status || 'published') !== 'archived')),
      blog_posts_drafts: JSON.stringify(initialBlogPosts.filter((b) => (b.status === 'draft' || b.status === 'archived'))),
      settings: JSON.stringify(settings),
      hero_messages: JSON.stringify(heroMessages),
      careers: JSON.stringify(careers),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load every block from cms_content, falling back to cached defaults when a row is missing
  useEffect(() => {
    let alive = true;
    (async () => {
      const email = await getAdminEmail();
      if (alive && email) {
        adminEmailRef.current = email;
        setIsAdmin(true);
      }
      try {
        const rows = await fetchCmsKeys([
          CMS_KEYS.content,
          CMS_KEYS.pageContent,
          CMS_KEYS.resources,
          CMS_KEYS.resourcesDrafts,
          CMS_KEYS.blogPosts,
          CMS_KEYS.blogPostsDrafts,
          CMS_KEYS.settings,
          CMS_KEYS.heroMessages,
          CMS_KEYS.careers,
        ]);

        // ── One-time migration of legacy localStorage data (admins only) ──
        if (email) {
          const pairs: Array<[string, string]> = [
            [CMS_KEYS.content, 'cms_content'],
            [CMS_KEYS.pageContent, 'cms_page_content'],
            [CMS_KEYS.resources, 'cms_resources'],
            [CMS_KEYS.settings, 'cms_settings'],
            [CMS_KEYS.heroMessages, 'cms_hero_messages'],
            [CMS_KEYS.careers, 'cms_careers'],
          ];
          for (const [dbKey, lsKey] of pairs) {
            if (rows[dbKey] !== undefined) continue;
            const raw = localStorage.getItem(lsKey);
            if (!raw) continue;
            try {
              const value = JSON.parse(raw);
              // Resources are cached as one combined list — split drafts out so they
              // never get written into the public-readable block.
              if (dbKey === CMS_KEYS.resources && Array.isArray(value)) {
                const pub = value.filter((r: any) => (r.status || 'published') !== 'draft' && (r.status || 'published') !== 'archived');
                const dr = value.filter((r: any) => (r.status === 'draft' || r.status === 'archived'));
                await upsertCmsKey(CMS_KEYS.resources, pub, email);
                if (dr.length) await upsertCmsKey(CMS_KEYS.resourcesDrafts, dr, email);
                rows[CMS_KEYS.resources] = pub;
                if (dr.length) rows[CMS_KEYS.resourcesDrafts] = dr;
                localStorage.removeItem(lsKey);
                continue;
              }
              await upsertCmsKey(dbKey, value, email);
              rows[dbKey] = value;
              localStorage.removeItem(lsKey);
            } catch {
              // skip malformed local value
            }
          }
        }

        if (!alive) return;

        if (rows[CMS_KEYS.content]) {
          lastSaved.current.content = JSON.stringify(rows[CMS_KEYS.content]);
          setContent(rows[CMS_KEYS.content] as CMSContent);
        }
        if (rows[CMS_KEYS.pageContent]) {
          lastSaved.current.page_content = JSON.stringify(rows[CMS_KEYS.pageContent]);
          setPageContent(rows[CMS_KEYS.pageContent] as Record<string, any>);
        }
        const publishedRows = (rows[CMS_KEYS.resources] as any[] | undefined) || null;
        const draftRows = (rows[CMS_KEYS.resourcesDrafts] as any[] | undefined) || null;
        if (publishedRows || draftRows) {
          const pub = (publishedRows || []).filter((r) => (r.status || 'published') !== 'draft' && (r.status || 'published') !== 'archived');
          const dr = draftRows || [];
          lastSaved.current.resources = JSON.stringify(pub);
          lastSaved.current.resources_drafts = JSON.stringify(dr);
          setResources([
            ...pub,
            ...dr.map((r: Record<string, unknown>) => ({ ...r, status: (r.status as string) || 'draft' })),
          ] as any[]);
        }
        const publishedBlogs = (rows[CMS_KEYS.blogPosts] as any[] | undefined) || null;
        const draftBlogs = (rows[CMS_KEYS.blogPostsDrafts] as any[] | undefined) || null;
        if (publishedBlogs || draftBlogs) {
          const pub = (publishedBlogs || []).filter((b) => (b.status || 'published') !== 'draft' && (b.status || 'published') !== 'archived');
          const dr = draftBlogs || [];
          lastSaved.current.blog_posts = JSON.stringify(pub);
          lastSaved.current.blog_posts_drafts = JSON.stringify(dr);
          setBlogPosts([
            ...pub,
            ...dr.map((b: Record<string, unknown>) => ({ ...b, status: (b.status as string) || 'draft' })),
          ] as BlogPost[]);
        }
        if (rows[CMS_KEYS.settings]) {
          lastSaved.current.settings = JSON.stringify(rows[CMS_KEYS.settings]);
          setSettings(rows[CMS_KEYS.settings] as CMSSettings);
        }
        if (rows[CMS_KEYS.heroMessages]) {
          const migratedHeroes = migrateHeroMessages(rows[CMS_KEYS.heroMessages] as HeroMessage[]);
          lastSaved.current.hero_messages = JSON.stringify(migratedHeroes);
          setHeroMessages(migratedHeroes);
        }
        if (rows[CMS_KEYS.careers]) {
          // Drop retired placeholder postings (career-1 / career-2) so they never
          // reappear in listings, job pages or the sitemap.
          const activeCareers = (rows[CMS_KEYS.careers] as Career[]).filter((c) => !isRetiredJob(c.id));
          lastSaved.current.careers = JSON.stringify(activeCareers);
          setCareers(activeCareers);
        }
      } catch {
        // network / RLS failure — cached values stay in place
      }
      if (alive) loadCandidates();
    })();
    return () => {
      alive = false;
    };
  }, [loadCandidates]);

  // Cache + persist resources (published and drafts are stored under separate keys
  // so draft resources never land in the public-readable block)
  useEffect(() => {
    localStorage.setItem('cms_resources', JSON.stringify(resources));
    const list = (resources || []) as any[];
    persist(CMS_KEYS.resources, list.filter((r) => (r.status || 'published') !== 'draft' && (r.status || 'published') !== 'archived'));
    persist(CMS_KEYS.resourcesDrafts, list.filter((r) => (r.status === 'draft' || r.status === 'archived')));
  }, [resources, persist]);

  // Cache + persist blog posts (same published/drafts split as resources)
  useEffect(() => {
    localStorage.setItem('cms_blog_posts', JSON.stringify(blogPosts));
    const list = (blogPosts || []) as BlogPost[];
    persist(CMS_KEYS.blogPosts, list.filter((b) => (b.status || 'published') !== 'draft' && (b.status || 'published') !== 'archived'));
    persist(CMS_KEYS.blogPostsDrafts, list.filter((b) => (b.status === 'draft' || b.status === 'archived')));
  }, [blogPosts, persist]);

  // Cache + persist settings
  useEffect(() => {
    localStorage.setItem('cms_settings', JSON.stringify(settings));
    persist(CMS_KEYS.settings, settings);
  }, [settings, persist]);

  // Cache + persist hero messages
  useEffect(() => {
    localStorage.setItem('cms_hero_messages', JSON.stringify(heroMessages));
    persist(CMS_KEYS.heroMessages, heroMessages);
  }, [heroMessages, persist]);

  // Cache + persist careers
  useEffect(() => {
    localStorage.setItem('cms_careers', JSON.stringify(careers));
    persist(CMS_KEYS.careers, careers);
  }, [careers, persist]);

  // Cache + persist page content
  useEffect(() => {
    localStorage.setItem('cms_page_content', JSON.stringify(pageContent));
    persist(CMS_KEYS.pageContent, pageContent);
  }, [pageContent, persist]);

  const saveContent = (newContent: CMSContent) => {
    setContent(newContent);
    localStorage.setItem('cms_content', JSON.stringify(newContent));
    persist(CMS_KEYS.content, newContent);
  };

  const updateHomeContent = (updates: Partial<CMSContent['home']>) => {
    const newContent = {
      ...content,
      home: { ...content.home, ...updates }
    };
    saveContent(newContent);
  };

  const updateAboutContent = (updates: Partial<CMSContent['about']>) => {
    const newContent = {
      ...content,
      about: { ...content.about, ...updates }
    };
    saveContent(newContent);
  };

  const updateContactContent = (updates: Partial<CMSContent['contact']>) => {
    const newContent = {
      ...content,
      contact: { ...content.contact, ...updates }
    };
    saveContent(newContent);
  };

  const getServiceContent = (serviceId: string) => {
    return content.services[serviceId] || {
      hero: {
        headline: '',
        subheadline: '',
        backgroundImage: '',
        ctaPrimary: 'Get Started',
        ctaSecondary: 'Learn More'
      },
      overview: {
        title: '',
        description: ''
      },
      process: {
        steps: []
      },
      techStack: {
        items: []
      },
      testimonials: {
        items: []
      },
      cta: {
        headline: '',
        description: '',
        buttonText: 'Contact Us',
        buttonLink: '/contact'
      }
    };
  };

  const updateServiceContent = (serviceId: string, serviceContent: any) => {
    const newContent = {
      ...content,
      services: {
        ...content.services,
        [serviceId]: serviceContent
      }
    };
    saveContent(newContent);
  };

  const getIndustryContent = (industryId: string) => {
    return content.industries[industryId] || {
      hero: {
        headline: '',
        subheadline: '',
        backgroundImage: ''
      },
      challenges: {
        items: []
      },
      solutions: {
        items: []
      },
      results: {
        stats: []
      },
      cta: {
        headline: '',
        description: '',
        buttonText: 'Get Started',
        buttonLink: '/contact'
      }
    };
  };

  const updateIndustryContent = (industryId: string, industryContent: any) => {
    const newContent = {
      ...content,
      industries: {
        ...content.industries,
        [industryId]: industryContent
      }
    };
    saveContent(newContent);
  };

  const getPageContent = (pageId: string) => {
    return pageContent[pageId] || defaultPageContent[pageId] || {};
  };

  const updatePageContent = (pageId: string, path: string, value: any) => {
    setPageContent(prev => {
      const current = prev[pageId] || defaultPageContent[pageId] || {};
      const keys = path.split('.');
      const updated = { ...current };
      let ref: any = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...ref[keys[i]] };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return { ...prev, [pageId]: updated };
    });
  };

  // Article management functions — backed by Supabase via ArticlesContext
  const addArticle = (article: any) => {
    addSupabaseArticle(article).catch((err) =>
      console.error('Failed to save article:', err)
    );
  };

  const updateArticle = (id: string, updatedArticle: any) => {
    updateSupabaseArticle(id, updatedArticle).catch((err) =>
      console.error('Failed to update article:', err)
    );
  };

  // Never-delete: archiving only flips status to 'archived' — the row is kept.
  const archiveArticle = (id: string) => {
    archiveSupabaseArticle(id).catch((err) =>
      console.error('Failed to archive article:', err)
    );
  };

  // Resource management functions
  const addResource = (resource: any) => {
    setResources((prev: any[]) => [resource, ...prev]);
  };

  const updateResource = (id: string, updatedResource: any) => {
    setResources((prev: any[]) =>
      prev.map((resource) => (resource.id === id ? { ...resource, ...updatedResource } : resource))
    );
  };

  // Never-delete: archiving a resource marks it 'archived' instead of removing it.
  const archiveResource = (id: string) => {
    setResources((prev: any[]) =>
      prev.map((resource) => (resource.id === id ? { ...resource, status: 'archived' } : resource))
    );
  };

  // Blog post management functions
  const addBlogPost = (post: BlogPost) => {
    setBlogPosts((prev) => [post, ...prev]);
  };

  const updateBlogPost = (id: string, updates: Partial<BlogPost>) => {
    setBlogPosts((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
  };

  // Never-delete: archiving a post marks it 'archived' instead of removing it.
  const archiveBlogPost = (id: string) => {
    setBlogPosts((prev) => prev.map((p) => (p.id === id ? { ...p, status: 'archived' } : p)));
  };

  // Settings management
  const updateSettings = (section: keyof CMSSettings, data: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: data
    }));
  };

  // Hero messages management
  const addHeroMessage = (message: HeroMessage) => {
    setHeroMessages(prev => [...prev, message]);
  };

  const updateHeroMessage = (id: string, updates: Partial<HeroMessage>) => {
    setHeroMessages(prev =>
      prev.map(msg => msg.id === id ? { ...msg, ...updates } : msg)
    );
  };

  // Never-delete: archiving a hero message marks it inactive instead of removing it.
  const archiveHeroMessage = (id: string) => {
    setHeroMessages(prev =>
      prev.map(msg => msg.id === id ? { ...msg, active: false } : msg)
    );
  };

  const reorderHeroMessages = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= heroMessages.length) return;
    setHeroMessages(prev => {
      const newMessages = [...prev];
      const [removed] = newMessages.splice(fromIndex, 1);
      newMessages.splice(toIndex, 0, removed);
      return newMessages;
    });
  };

  // Careers management
  const addCareer = (career: Career) => {
    setCareers(prev => [career, ...prev]);
  };

  const updateCareer = (id: string, updates: Partial<Career>) => {
    setCareers(prev =>
      prev.map(career => career.id === id ? { ...career, ...updates } : career)
    );
  };

  // Never-delete: archiving a position marks it inactive instead of removing it.
  const archiveCareer = (id: string) => {
    setCareers(prev =>
      prev.map(career => career.id === id ? { ...career, active: false } : career)
    );
  };

  // Candidates management — backed by public.job_applications
  const addCandidate = (candidate: Candidate) => {
    setCandidates((prev) => [candidate, ...prev]);
  };

  const updateCandidate = async (id: string, updates: Partial<Candidate>) => {
    const current = candidates.find((c) => c.id === id);
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.status !== undefined) row.status = updates.status;
    if (updates.notes !== undefined) row.notes = updates.notes;
    if (updates.rating !== undefined || updates.source !== undefined) {
      row.data = {
        ...(current?.data || {}),
        ...(updates.rating !== undefined ? { rating: updates.rating } : {}),
        ...(updates.source !== undefined ? { source: updates.source } : {}),
      };
    }
    try {
      const { error } = await supabase.from('job_applications').update(row).eq('id', id);
      if (error) throw error;
    } catch {
      loadCandidates();
    }
  };

  // Never-delete: archiving a candidate marks the application 'archived' instead of removing it.
  const archiveCandidate = async (id: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'archived' as Candidate['status'] } : c)));
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    } catch {
      loadCandidates();
    }
  };

  const value = {
    content,
    updateHomeContent,
    updateAboutContent,
    updateContactContent,
    getServiceContent,
    updateServiceContent,
    getIndustryContent,
    updateIndustryContent,
    getPageContent,
    updatePageContent,
    articles,
    articlesLoading,
    addArticle,
    updateArticle,
    archiveArticle,
    resources,
    addResource,
    updateResource,
    archiveResource,
    blogPosts,
    addBlogPost,
    updateBlogPost,
    archiveBlogPost,
    settings,
    updateSettings,
    heroMessages,
    addHeroMessage,
    updateHeroMessage,
    archiveHeroMessage,
    reorderHeroMessages,
    careers,
    addCareer,
    updateCareer,
    archiveCareer,
    candidates,
    addCandidate,
    updateCandidate,
    archiveCandidate,
  };

  return createElement(CMSContentContext.Provider, { value }, children);
}

export function useCMSContent() {
  const ctx = useContext(CMSContentContext);
  if (!ctx) throw new Error('useCMSContent must be used within CMSContentProvider');
  return ctx;
}